import React, { useState, useEffect } from 'react';
import * as Tone from 'tone';
import CodeCard from './CodeCard';
import './CodeCard.css';

// Create effects - Add Reverb
const reverb = new Tone.Reverb({
  decay: 1.5, // A bit of reverb tail
  wet: 0.3    // Adjust mix between dry and wet signal
}).toDestination();

// Create a synth instance using Tone.js - Use FMSynth for smoother/richer tones
const synth = new Tone.FMSynth({
  harmonicity: 1.5, // Adds some tonal complexity
  modulationIndex: 5, // Controls the FM amount
  oscillator: {
    type: 'sine' // Base oscillator type
  },
  envelope: { // Main sound envelope
    attack: 0.01,  // Slightly smoother attack than default
    decay: 0.3,    // Longer decay
    sustain: 0.1,  // Lower sustain level
    release: 0.5   // Longer release for fade out
  },
  modulation: { // Modulating oscillator
    type: 'triangle' 
  },
  modulationEnvelope: { // Envelope for the modulation
    attack: 0.01,
    decay: 0.2,
    sustain: 0.1,
    release: 0.5
  }
}).connect(reverb); // Connect synth through reverb to speakers

const CodeCardGenerator = () => {
  const [codes, setCodes] = useState([]);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Ensure Tone.js AudioContext is started on user interaction
  // Browsers often require user interaction before starting audio
  useEffect(() => {
    const startAudioContext = async () => {
      await Tone.start();
      console.log('Audio context started');
    };
    // Add event listener to start audio context on the first click/interaction
    document.body.addEventListener('click', startAudioContext, { once: true });
    document.body.addEventListener('touchstart', startAudioContext, { once: true });

    return () => {
      // Cleanup listeners when component unmounts
      document.body.removeEventListener('click', startAudioContext);
      document.body.removeEventListener('touchstart', startAudioContext);
    };
  }, []);
  
  // Generate a code with 3 unique digits from 1-4
  const generateUniqueDigitsCode = () => {
    // Create an array of the digits 1 through 4
    const availableDigits = [1, 2, 3, 4];
    let code = [];
    
    // Select 3 unique digits from the available digits
    for (let i = 0; i < 3; i++) {
      // Get a random index from the remaining available digits
      const randomIndex = Math.floor(Math.random() * availableDigits.length);
      // Add the digit at that index to our code
      code.push(availableDigits[randomIndex]);
      // Remove the used digit from available digits
      availableDigits.splice(randomIndex, 1);
    }
    
    return code;
  };
  
  const handleGenerateCode = () => {
    // Generate a new code
    const newCode = generateUniqueDigitsCode();
    
    // Always replace any existing code with the new one
    setCodes([newCode]);
    
    // Reset to front side when generating a new card
    setIsFlipped(false);
  };
  
  const handleFlipCard = () => {
    // Ask for confirmation only when showing the code
    if (isFlipped) {
      setShowConfirmation(true);
      return;
    }
    
    // If we're flipping to hide the code, no confirmation needed
    flipCardWithSound();
  };
  
  const handleConfirm = () => {
    setShowConfirmation(false);
    flipCardWithSound();
  };
  
  const handleCancel = () => {
    setShowConfirmation(false);
  };
  
  const flipCardWithSound = () => {
    // Play a smoother notification sound using Tone.js
    if (Tone.context.state === 'running') { 
      // Play a slightly lower note (A4) for a shorter duration (16th note)
      synth.triggerAttackRelease('A4', '16n'); 
    } else {
      // Attempt to start context if not running
      Tone.start().then(() => {
        synth.triggerAttackRelease('A4', '16n');
      }).catch(error => {
        console.error("Failed to start Tone.js context or play sound:", error);
      });
    }

    setIsFlipped(!isFlipped);
  };
  
  return (
    <div className="code-card-generator">
      <div className="code-generator-buttons">
        <button className="generate-code-button" onClick={handleGenerateCode}>
          Generate Code Card
        </button>
        {codes.length > 0 && (
          <button className="flip-button" onClick={handleFlipCard}>
            {isFlipped ? "Show Code" : "Flip Card"}
          </button>
        )}
      </div>
    
      <div className="code-cards-display">
        {codes.length === 0 ? (
          <div className="code-card-placeholder">
            <span>No code cards generated</span>
          </div>
        ) : (
          <div className="code-card-list">
            {codes.map((code, index) => (
              <CodeCard 
                key={index} 
                code={code} 
                isFlipped={isFlipped}
                showConfirmation={showConfirmation}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeCardGenerator; 